import { forwardRef, useCallback, useRef, useState } from "react";
import type { ComponentType, ForwardedRef, ReactNode, Ref } from "react";
import { useDrag, useDrop } from "react-dnd";
import type { DropTargetMonitor } from "react-dnd";
import { getCssVariableHandler } from "../../utils/styles/getCssVariables";
import type { DropSide } from "../../utils/methods/columnOrder";
import {
  TABLE_COLUMN_DND_TYPE,
  useColumnReorder,
} from "./columnReorderContext";
import type { ColumnDragItem } from "./columnReorderContext";

function setRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (!ref) return;
  if (typeof ref === "function") ref(value);
  else (ref as { current: T | null }).current = value;
}

type CellProps = {
  columnId: string;
  sx?: Record<string, unknown>;
  children?: ReactNode;
  [key: string]: unknown;
};

/**
 * Wraps a table header cell component so the column can be dragged and dropped
 * onto another header to reorder it. Drop side (before/after) follows the cursor
 * relative to the target's midpoint and is shown as a pink edge on the target.
 * `moveColumn` comes from the nearest `ColumnReorderProvider`; without one the
 * cell renders as a plain header.
 *
 * Nested controls keep working: a click never starts a drag, and the resize
 * handle stops the mousedown so grabbing it does not drag the column.
 */
export function createDraggableHeaderCell<P extends { sx?: unknown }>(
  CellComponent: ComponentType<P>,
) {
  return forwardRef<HTMLTableCellElement, CellProps>(
    function DraggableHeaderCell(
      { columnId, sx, ...rest }: CellProps,
      forwardedRef: ForwardedRef<HTMLTableCellElement>,
    ) {
      const reorder = useColumnReorder();
      const nodeRef = useRef<HTMLTableCellElement | null>(null);
      const [side, setSide] = useState<DropSide>("before");

      const [{ isDragging }, drag] = useDrag(
        () => ({
          type: TABLE_COLUMN_DND_TYPE,
          item: { id: columnId } as ColumnDragItem,
          canDrag: () => Boolean(reorder),
          collect: (monitor) => ({ isDragging: monitor.isDragging() }),
        }),
        [columnId, reorder],
      );

      const sideFromPointer = (monitor: DropTargetMonitor): DropSide => {
        const rect = nodeRef.current?.getBoundingClientRect();
        const x = monitor.getClientOffset()?.x;
        if (!rect || x == null) return "before";
        return x < rect.left + rect.width / 2 ? "before" : "after";
      };

      const [{ isOver }, drop] = useDrop<
        ColumnDragItem,
        void,
        { isOver: boolean }
      >(
        () => ({
          accept: TABLE_COLUMN_DND_TYPE,
          canDrop: (item) => item.id !== columnId,
          hover: (item, monitor) => {
            if (item.id === columnId) return;
            const next = sideFromPointer(monitor);
            setSide((prev) => (prev === next ? prev : next));
          },
          drop: (item, monitor) => {
            if (item.id === columnId) return;
            reorder?.moveColumn(item.id, columnId, sideFromPointer(monitor));
          },
          collect: (monitor) => ({
            isOver: monitor.isOver() && monitor.canDrop(),
          }),
        }),
        [columnId, reorder],
      );

      const attachRef = useCallback(
        (node: HTMLTableCellElement | null) => {
          nodeRef.current = node;
          drag(drop(node));
          setRef(forwardedRef, node);
        },
        [drag, drop, forwardedRef],
      );

      // The dashboard header row is pink, so the drop indicator is white.
      const indicator = getCssVariableHandler({ variableName: "--white-rgb" });
      const dndSx = reorder
        ? {
            cursor: isDragging ? "grabbing" : "grab",
            userSelect: "none",
            opacity: isDragging ? 0.4 : 1,
            ...(isOver
              ? {
                  boxShadow:
                    side === "before"
                      ? `inset 4px 0 0 0 ${indicator}`
                      : `inset -4px 0 0 0 ${indicator}`,
                }
              : {}),
          }
        : {};

      const Cell = CellComponent as ComponentType<Record<string, unknown>>;
      return (
        <Cell
          ref={attachRef}
          data-column={columnId}
          {...rest}
          sx={{ ...(sx || {}), ...dndSx }}
        />
      );
    },
  );
}
